export type DrawerSide = 'left' | 'right';
export type DrawerIcon = 'controls' | 'info' | 'palette';

export interface MobileDrawerOptions {
  side: DrawerSide;
  icon: DrawerIcon;
  /** Optional aria-label for the toggle button. */
  label?: string;
}

export interface MobileDrawerHandle {
  destroy(): void;
}

const ICONS: Record<DrawerIcon, string> = {
  controls: '☰',
  info: 'ⓘ',
  palette: '◐',
};

interface ScrimState {
  el: HTMLElement;
  refs: number;
  openDrawers: Set<HTMLElement>;
}
let scrim: ScrimState | null = null;

function ensureScrim(): ScrimState {
  if (scrim && scrim.el.isConnected) return scrim;
  if (scrim) {
    // stale (DOM was cleared externally) — drop and recreate
    scrim = null;
  }
  const el = document.createElement('div');
  el.setAttribute('data-mobile-drawer-scrim', '');
  el.addEventListener('click', () => {
    if (!scrim) return;
    for (const d of scrim.openDrawers) d.setAttribute('data-open', 'false');
    scrim.openDrawers.clear();
    el.setAttribute('data-active', 'false');
  });
  document.body.appendChild(el);
  scrim = { el, refs: 0, openDrawers: new Set() };
  return scrim;
}

function releaseScrim(): void {
  if (!scrim) return;
  scrim.refs -= 1;
  if (scrim.refs <= 0) {
    scrim.el.remove();
    scrim = null;
  }
}

/**
 * At ≤720px, wraps the given element(s) in a fixed-position drawer with an
 * edge-anchored toggle button. The CSS hides the drawer container + toggle
 * above 720px, so on desktop the panels appear hidden and the demo's
 * existing per-panel CSS governs (each demo also hides its desktop
 * floating .panel at ≤720px via the kit's chrome.css).
 *
 * Returning destroy() restores elements to their original DOM parents.
 */
export function mountMobileDrawer(
  elements: HTMLElement | HTMLElement[],
  opts: MobileDrawerOptions,
): MobileDrawerHandle {
  const list = Array.isArray(elements) ? elements : [elements];
  const originalParents = list.map((e) => ({
    el: e,
    parent: e.parentElement,
    next: e.nextSibling,
  }));

  const container = document.createElement('div');
  container.setAttribute('data-mobile-drawer', opts.side);
  container.setAttribute('data-open', 'false');
  document.body.appendChild(container);
  for (const el of list) container.appendChild(el);

  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.setAttribute('data-mobile-drawer-toggle', opts.side);
  toggle.setAttribute('aria-label', opts.label ?? `Toggle ${opts.side} panel`);
  toggle.textContent = ICONS[opts.icon];
  document.body.appendChild(toggle);

  const scrimState = ensureScrim();
  scrimState.refs += 1;

  toggle.addEventListener('click', () => {
    const isOpen = container.getAttribute('data-open') === 'true';
    if (isOpen) {
      container.setAttribute('data-open', 'false');
      scrimState.openDrawers.delete(container);
      if (scrimState.openDrawers.size === 0) {
        scrimState.el.setAttribute('data-active', 'false');
      }
    } else {
      container.setAttribute('data-open', 'true');
      scrimState.openDrawers.add(container);
      scrimState.el.setAttribute('data-active', 'true');
    }
  });

  return {
    destroy(): void {
      for (const { el, parent, next } of originalParents) {
        if (parent) parent.insertBefore(el, next);
      }
      container.remove();
      toggle.remove();
      releaseScrim();
    },
  };
}
